import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { CloseButton, ConfirmationModal, Typography, useOverlayContext } from 'seagull';
import { QBOSetupStep } from '~/components/VendorOnboardingModal/QBOSetupStep';
import { useEventReporter } from '~/event-reporter';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useSwitchBusiness } from '~/hooks/useSwitchBusiness';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions, useStoreStatic } from '~/store/store-context';
import { VobQboState } from '~/types/api.model';
import { FirstStep } from './FirstStep';
import { QBOStep } from './QBOStep';
import { SavingStep } from './SavingStep';
import { SecondStep } from './SecondStep';
import styles from './VendorOnboardingModal.module.scss';
import { ErrorType, Step } from './VendorOnboardingModal.utils';
export default function VendorOnboardingModal(props) {
    const { dismissModal, vobQboState, showQboStep } = props;
    const { getState } = useStoreStatic();
    const workspaces = useStore(selectors.user.workspaces);
    const { logBI } = useStoreActions();
    const overlayContext = useOverlayContext();
    const { switchBusiness } = useSwitchBusiness();
    const { reportToHeap, reportToHubspot, reportToAppcues, sendEventToGtm } = useEventReporter();
    const { BUSINESS_DESCRIPTION_ONBOARDING } = useFeatureFlags();
    const isLinkedToQbo = useStore(selectors.integration.isLinkedToQbo);
    const [step, setStep] = useState(vobQboState === VobQboState.Started ? Step.QBO : vobQboState === VobQboState.ConnectionTriggered && isLinkedToQbo ? Step.QBOSetup : Step.First);
    const shouldShowCloseButton = step !== Step.QBO && step !== Step.QBOSetup && workspaces && workspaces.some((workspace)=>workspace.isCompletelySetup());
    const [vobData, setVobData] = useState(()=>{
        var _ref, _ref1, _ref2;
        var _userInfo_business;
        const { userInfo } = getState();
        const postalAddress = userInfo === null || userInfo === void 0 ? void 0 : (_userInfo_business = userInfo.business()) === null || _userInfo_business === void 0 ? void 0 : _userInfo_business.postalAddress;
        return {
            name: {
                first: (_ref = userInfo === null || userInfo === void 0 ? void 0 : userInfo.firstName()) !== null && _ref !== void 0 ? _ref : '',
                last: (_ref1 = userInfo === null || userInfo === void 0 ? void 0 : userInfo.lastName()) !== null && _ref1 !== void 0 ? _ref1 : ''
            },
            companyName: (_ref2 = userInfo === null || userInfo === void 0 ? void 0 : userInfo.businessName()) !== null && _ref2 !== void 0 ? _ref2 : '',
            postalAddress: {
                address1: (postalAddress === null || postalAddress === void 0 ? void 0 : postalAddress.addressOne) || '',
                address2: (postalAddress === null || postalAddress === void 0 ? void 0 : postalAddress.addressTwo) || '',
                city: (postalAddress === null || postalAddress === void 0 ? void 0 : postalAddress.city) || '',
                state: (postalAddress === null || postalAddress === void 0 ? void 0 : postalAddress.state) || '',
                postalCode: (postalAddress === null || postalAddress === void 0 ? void 0 : postalAddress.postalCode) || '',
                country: (postalAddress === null || postalAddress === void 0 ? void 0 : postalAddress.country) || 'US'
            },
            phone: (userInfo === null || userInfo === void 0 ? void 0 : userInfo.phone()) || '',
            businessTypes: []
        };
    });
    const onClose = (value)=>{
        reportToAppcues('form-success', 'first_login');
        dismissModal(value);
    };
    async function onXClose() {
        logBI(18103);
        reportToHeap('preliminary-onboarding', 'click', 'user-click-quit-vendor-onboarding');
        const shouldSwitchToOtherBusiness = await overlayContext.showModal({
            biName: 'quit_confirmation',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    title: "Are you sure?",
                    content: /*#__PURE__*/ _jsx(Typography, {
                        children: "Once you quit the Service Provider onboarding process, you will be redirected to your existing account."
                    }),
                    primaryButtonLabel: "Quit onboarding process",
                    onPrimaryButtonClick: ()=>{
                        logBI(18104);
                        reportToHeap('preliminary-onboarding', 'click', 'user-click-quit-vendor-onboarding-approve');
                        reportToHubspot('create_vendor_account_canceled_v3');
                        dismissModal(true);
                    },
                    secondaryButtonLabel: "Cancel",
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    onCloseButtonClick: ()=>dismissModal(false),
                    bi: {
                        primaryButton: {
                            name: 'quit_onboarding'
                        },
                        secondaryButton: {
                            name: 'cancel_quit'
                        },
                        closeButton: {
                            name: 'close'
                        }
                    },
                    testId: "quit-confirmation"
                });
            }
        });
        const firstValidWorkspace = workspaces && workspaces.find((workspace)=>workspace.isCompletelySetup());
        if (shouldSwitchToOtherBusiness && firstValidWorkspace) {
            await switchBusiness(firstValidWorkspace.id());
            dismissModal(true);
        }
    }
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": "vendor-onboarding-modal",
        className: styles.root,
        children: [
            shouldShowCloseButton && /*#__PURE__*/ _jsx(CloseButton, {
                className: styles.closeBtn,
                skin: "transparent",
                testId: "close-btn",
                onClick: onXClose,
                bi: {
                    name: 'close'
                }
            }),
            step === Step.First ? /*#__PURE__*/ _jsx(FirstStep, {
                initialState: vobData,
                onSubmit: (formData)=>{
                    var _formData_numOfInvoices;
                    var _formData_name, _formData_name1;
                    const biLogData = _object_spread_props(_object_spread({
                        firstName: (_formData_name = formData.name) === null || _formData_name === void 0 ? void 0 : _formData_name.first,
                        lastName: (_formData_name1 = formData.name) === null || _formData_name1 === void 0 ? void 0 : _formData_name1.last,
                        companyName: formData.companyName,
                        businessType: formData.businessTypes
                    }, BUSINESS_DESCRIPTION_ONBOARDING && {
                        businessDescription: formData.businessDescription
                    }), {
                        numOfInvoices: formData.numOfInvoices
                    });
                    logBI(10325, biLogData);
                    sendEventToGtm('onboarding_business_details', {
                        company_name: formData.companyName,
                        company_services: BUSINESS_DESCRIPTION_ONBOARDING ? formData.businessDescription || formData.businessTypes.join(', ') : formData.businessTypes.join(', '),
                        monthly_invoices: parseInt((_formData_numOfInvoices = formData.numOfInvoices) !== null && _formData_numOfInvoices !== void 0 ? _formData_numOfInvoices : '0', 10)
                    });
                    const newFormData = _object_spread({}, vobData, formData);
                    setVobData(newFormData);
                    setStep(Step.Second);
                }
            }) : null,
            step === Step.Second ? /*#__PURE__*/ _jsx(SecondStep, {
                initialState: vobData,
                onSubmit: (formData)=>{
                    var _ref, _ref1, _ref2;
                    var _vobData_name, _vobData_name1, _getState_userInfo;
                    logBI(10326, formData);
                    sendEventToGtm('onboarding_business_address', {
                        company_city: formData.postalAddress.city,
                        company_state: formData.postalAddress.state,
                        company_zipcode: parseInt(formData.postalAddress.postalCode, 10),
                        phone: formData.phone,
                        first_name: (_ref = (_vobData_name = vobData.name) === null || _vobData_name === void 0 ? void 0 : _vobData_name.first) !== null && _ref !== void 0 ? _ref : '',
                        last_name: (_ref1 = (_vobData_name1 = vobData.name) === null || _vobData_name1 === void 0 ? void 0 : _vobData_name1.last) !== null && _ref1 !== void 0 ? _ref1 : '',
                        email: (_ref2 = (_getState_userInfo = getState().userInfo) === null || _getState_userInfo === void 0 ? void 0 : _getState_userInfo.email()) !== null && _ref2 !== void 0 ? _ref2 : ''
                    });
                    const newFormData = _object_spread({}, vobData, formData);
                    setVobData(newFormData);
                    setStep(Step.Saving);
                }
            }) : null,
            step === Step.Saving ? /*#__PURE__*/ _jsx(SavingStep, {
                vobData: vobData,
                onSavingSuccess: showQboStep ? ()=>setStep(Step.QBO) : onClose,
                onErrorBackButton: (errorType)=>{
                    if (errorType === ErrorType.General) {
                        setStep(Step.First);
                    } else {
                        setStep(Step.Second);
                    }
                }
            }) : null,
            step === Step.QBO ? /*#__PURE__*/ _jsx(QBOStep, {
                onCancel: onClose,
                vobQboState: vobQboState
            }) : null,
            step === Step.QBOSetup ? /*#__PURE__*/ _jsx(QBOSetupStep, {
                onContinue: onClose
            }) : null
        ]
    });
}
